import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { bookContent } from '../data/bookContent';
import { ScriptureQuote } from '../components/ScriptureQuote';
import { PrayerCard } from '../components/PrayerCard';
import { JournalPrompt } from '../components/JournalPrompt';
import { AffirmationCard } from '../components/AffirmationCard';
import { QuoteBlock } from '../components/QuoteBlock';
import { ChapterNavigation } from '../components/ChapterNavigation';
import { AudioNarration } from '../components/AudioNarration';
import { PrintView } from '../components/PrintView';

export const ChapterView = () => {
  const { chapterId } = useParams();
  const navigate = useNavigate();
  const chapterNum = parseInt(chapterId, 10);
  
  const chapter = bookContent.chapters.find(c => c.id === chapterNum);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [chapterId]);

  if (!chapter) {
    navigate('/contents');
    return null;
  }

  // Prepare full text for audio narration
  const fullChapterText = `${chapter.scripture.text}. ${chapter.scripture.reference}. 
    ${chapter.content} 
    ${chapter.quote} 
    Affirmation: ${chapter.affirmation}`;

  return (
    <div data-testid={`chapter-${chapterNum}`} className="min-h-screen bg-cream pb-24">
      {/* Hero Image */}
      <div className="relative h-64 md:h-80 overflow-hidden">
        <img 
          src={chapter.image} 
          alt={chapter.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-cream via-cream/50 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-3xl mx-auto">
            <span className="inline-block px-3 py-1 bg-gold/20 text-gold text-sm font-medium rounded-full mb-3">
              Chapter {chapter.id}
            </span>
            <h1 className="font-heading text-4xl md:text-5xl text-charcoal animate-fade-in">
              {chapter.title}
            </h1>
            <p className="font-heading text-xl text-charcoal-light italic mt-2">
              {chapter.subtitle}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Audio & Print Controls */}
        <div className="mb-8 space-y-4">
          <AudioNarration 
            text={fullChapterText} 
            title={`Chapter ${chapter.id}: ${chapter.title}`} 
          />
          <div className="flex justify-end">
            <PrintView chapterId={chapter.id} />
          </div>
        </div>

        {/* Opening Scripture */}
        <ScriptureQuote 
          text={chapter.scripture.text} 
          reference={chapter.scripture.reference} 
        />

        {/* Main Content */}
        <div className="prose prose-lg max-w-none my-10">
          {chapter.content.split('\n\n').map((paragraph, index) => (
            <p 
              key={index} 
              className="text-charcoal/90 leading-relaxed mb-6 animate-fade-in"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {paragraph}
            </p>
          ))}
        </div>

        {/* Quote */}
        <QuoteBlock quote={chapter.quote} />

        {/* Affirmation */}
        <AffirmationCard affirmation={chapter.affirmation} />

        {/* Journal Section */}
        <div className="my-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-sage/20 flex items-center justify-center">
              <svg className="w-5 h-5 text-sage" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <h2 className="font-heading text-2xl text-charcoal">Reflection Questions</h2>
          </div>
          <p className="text-charcoal-light mb-6">
            Take time to honestly reflect on these questions. Your responses are saved privately on your device.
          </p>
          {chapter.journalQuestions.map((question, index) => (
            <JournalPrompt 
              key={index}
              question={question}
              chapterId={chapter.id}
              questionIndex={index}
            />
          ))}
        </div>

        {/* Closing Prayer */}
        <PrayerCard prayer={chapter.prayer} />

        {/* Closing Benediction (for final chapter) */}
        {chapter.closingBenediction && (
          <div className="my-12 p-8 md:p-12 bg-gradient-to-br from-gold-light/30 to-cream rounded-2xl border border-gold/20">
            <h2 className="font-heading text-3xl text-charcoal mb-6 text-center">
              A Final Word
            </h2>
            <div className="prose prose-lg max-w-none">
              {chapter.closingBenediction.split('\n\n').map((paragraph, index) => (
                <p key={index} className="text-charcoal/90 leading-relaxed mb-4 last:mb-0">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <ChapterNavigation currentChapter={chapterNum} />
    </div>
  );
};
